import {
  convexQuery,
  useConvexAction,
  useConvexMutation,
} from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import type { InterviewNoteWithAuthor } from '@cvx/features/interviews/interview_notes_db'
import type { TranscriptChunk } from '@cvx/features/interviews/interviews'
import { Button } from '@rekrytering/ui/components/button'
import { cn } from '@rekrytering/ui/lib/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PdfExportDropdown } from '@/features/interviews/components/pdf-export-dropdown'
import { RagChatPanel } from '@/features/interviews/components/rag-chat-panel'
import { useThumbnailExtractor } from '@/features/interviews/hooks/use-thumbnail-extractor'

const SCORE_MAX = 5

const ScoreBar = ({ score }: { score: number }) => (
  <div className="flex gap-1">
    {Array.from({ length: SCORE_MAX }).map((_, i) => (
      <div
        key={i}
        className={cn(
          'h-2 flex-1 rounded-full',
          i < score ? 'bg-primary' : 'bg-border',
        )}
      />
    ))}
  </div>
)

const formatDurationSec = (durationSec: number): string => {
  const minPart = Math.floor(durationSec / 60)
  const secPart = durationSec % 60
  return `${minPart}m ${secPart}s`
}

const formatTimestamp = (ms: number): string =>
  new Date(ms).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

const TranscriptPanel = ({
  chunks,
  currentTimeSec,
  onChunkClick,
}: {
  chunks: TranscriptChunk[]
  currentTimeSec: number
  onChunkClick: (startSec: number) => void
}) => {
  const activeIndex = chunks.findIndex(
    (chunk) =>
      chunk.startSec !== undefined &&
      chunk.endSec !== undefined &&
      currentTimeSec >= chunk.startSec &&
      currentTimeSec <= chunk.endSec,
  )

  const activeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [activeIndex])

  return (
    <div className="flex max-h-96 flex-col gap-1 overflow-y-auto rounded-lg border border-border bg-muted/30 p-3">
      {chunks.map((chunk, i) => (
        <button
          key={chunk._id}
          ref={i === activeIndex ? activeRef : undefined}
          type="button"
          onClick={() => {
            if (chunk.startSec !== undefined) onChunkClick(chunk.startSec)
          }}
          className={cn(
            'rounded px-2 py-1 text-left text-sm leading-relaxed transition-colors',
            i === activeIndex
              ? 'bg-primary/10 text-primary'
              : 'text-primary/70 hover:bg-muted hover:text-primary',
          )}
        >
          {chunk.startSec !== undefined && (
            <span className="mr-2 text-xs font-mono text-primary/40">
              {Math.floor(chunk.startSec / 60)}:
              {String(Math.floor(chunk.startSec % 60)).padStart(2, '0')}
            </span>
          )}
          {chunk.text}
        </button>
      ))}
    </div>
  )
}

const NotesPanel = ({
  interviewId,
  notes,
}: {
  interviewId: Id<'interviews'>
  notes: InterviewNoteWithAuthor[]
}) => {
  const { t } = useTranslation()
  const [newNoteText, setNewNoteText] = useState('')

  const { mutate: addNote } = useMutation({
    mutationFn: useConvexMutation(
      api.features.interviews.interview_notes_db.addMyInterviewNote,
    ),
  })

  const { mutate: deleteNote } = useMutation({
    mutationFn: useConvexMutation(
      api.features.interviews.interview_notes_db.deleteMyInterviewNote,
    ),
  })

  const handleAddNote = (): void => {
    const text = newNoteText.trim()
    if (!text) return
    addNote({ interviewId, text })
    setNewNoteText('')
  }

  return (
    <div className="flex flex-col gap-3">
      {notes.length === 0 ? (
        <p className="text-sm text-primary/40">
          {t('interviews.report.noNotes')}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {notes.map((note) => (
            <div
              key={note._id}
              className="flex items-start justify-between gap-3 rounded-lg border border-border bg-muted/30 p-3"
            >
              <div className="flex flex-col gap-1 min-w-0">
                <p className="text-sm text-primary/80 break-words">
                  {note.text}
                </p>
                <p className="text-xs text-primary/40">
                  {note.authorName} · {formatTimestamp(note.createdAt)}
                </p>
              </div>
              {note.isOwnNote && (
                <button
                  type="button"
                  onClick={() =>
                    deleteNote({ noteId: note._id as Id<'interviewNotes'> })
                  }
                  className="shrink-0 text-xs text-primary/40 hover:text-destructive transition-colors"
                >
                  {t('interviews.report.deleteNote')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          value={newNoteText}
          onChange={(e) => setNewNoteText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAddNote()
          }}
          placeholder={t('interviews.report.notePlaceholder')}
          className="flex-1 rounded-md border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30"
        />
        <Button
          size="sm"
          variant="outline"
          onClick={handleAddNote}
          disabled={!newNoteText.trim()}
        >
          {t('interviews.report.addNote')}
        </Button>
      </div>
    </div>
  )
}

export const InterviewReportPage = () => {
  const { t } = useTranslation()
  const { interviewId } = useParams({ strict: false }) as {
    interviewId: Id<'interviews'>
  }
  const [copiedLink, setCopiedLink] = useState(false)
  const [currentTimeSec, setCurrentTimeSec] = useState(0)
  const videoRef = useRef<HTMLVideoElement>(null)

  const { data: report } = useQuery(
    convexQuery(api.features.interviews.interviews.getMyInterviewReport, {
      interviewId,
    }),
  )

  const { mutateAsync: fetchVideoUrls, data: videoUrlsResult } = useMutation({
    mutationFn: useConvexAction(
      api.features.interviews.get_interview_video_urls.getMyInterviewVideoUrls,
    ),
  })

  useEffect(() => {
    if (report?.interview.recordingR2Key) {
      fetchVideoUrls({ interviewId })
    }
  }, [report?.interview.recordingR2Key, interviewId, fetchVideoUrls])

  const needsThumbnail =
    report?.interview.recordingR2Key != null &&
    report?.interview.thumbnailR2Key == null

  const recordingUrlForThumbnail =
    needsThumbnail && videoUrlsResult?.ok === true
      ? videoUrlsResult.recordingUrl
      : null

  const { startExtraction } = useThumbnailExtractor({
    interviewId,
    recordingUrl: recordingUrlForThumbnail ?? '',
    durationSec: report?.interview.durationSec ?? 0,
    onThumbnailSaved: (_key: string) => undefined,
  })

  useEffect(() => {
    if (recordingUrlForThumbnail) {
      startExtraction()
    }
  }, [recordingUrlForThumbnail, startExtraction])

  const { data: notesAndViewers } = useQuery(
    convexQuery(
      api.features.interviews.interview_notes_db.listMyInterviewNotesAndViewers,
      { interviewId },
    ),
  )

  const { mutate: recordView } = useMutation({
    mutationFn: useConvexMutation(
      api.features.interviews.interview_notes_db.recordMyInterviewView,
    ),
  })

  useEffect(() => {
    recordView({ interviewId })
  }, [interviewId, recordView])

  const { mutateAsync: enableSharing } = useMutation({
    mutationFn: useConvexMutation(
      api.features.interviews.report_sharing.enableMyReportSharing,
    ),
  })

  const { mutateAsync: disableSharing } = useMutation({
    mutationFn: useConvexMutation(
      api.features.interviews.report_sharing.disableMyReportSharing,
    ),
  })

  const handleSeekVideo = useCallback((seekToSec: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = seekToSec
      videoRef.current.play()
    }
  }, [])

  if (!report) return null

  const {
    interview,
    candidate,
    job,
    competencyScores,
    transcriptChunks,
    clips,
  } = report

  const shareUrl =
    interview.shareToken !== undefined
      ? `${window.location.origin}/r/${interview.shareToken}`
      : null

  const recordingUrl =
    videoUrlsResult?.ok === true ? videoUrlsResult.recordingUrl : null

  const clipUrls = videoUrlsResult?.ok === true ? videoUrlsResult.clips : []

  const handleCopyShareLink = async (): Promise<void> => {
    if (!shareUrl) return
    await navigator.clipboard.writeText(shareUrl)
    setCopiedLink(true)
    setTimeout(() => setCopiedLink(false), 2000)
  }

  const handleToggleSharing = async (): Promise<void> => {
    if (interview.isPublic) {
      await disableSharing({ interviewId })
    } else {
      await enableSharing({ interviewId })
    }
  }

  return (
    <div className="flex h-full w-full flex-col px-6 py-8">
      <div className="mx-auto flex h-full w-full max-w-(--breakpoint-xl) flex-col gap-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="text-2xl font-medium text-primary">
              {candidate.name}
            </h2>
            <p className="text-sm text-primary/60">
              {job.title} · {candidate.email}
            </p>
            {notesAndViewers && notesAndViewers.viewers.length > 0 && (
              <p className="text-xs text-primary/40 mt-1">
                {t('interviews.report.reviewedBy')}:{' '}
                {notesAndViewers.viewers.map((v) => v.name).join(', ')}
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            {interview.completedAt !== undefined && (
              <span className="text-sm text-primary/50">
                {t('interviews.report.completedAt')}:{' '}
                {formatTimestamp(interview.completedAt)}
              </span>
            )}
            {interview.durationSec !== undefined && (
              <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-primary/70">
                {formatDurationSec(interview.durationSec)}
              </span>
            )}
            <PdfExportDropdown jobId={job._id} />
            <Button
              variant={interview.isPublic ? 'secondary' : 'outline'}
              size="sm"
              onClick={handleToggleSharing}
            >
              {interview.isPublic
                ? t('interviews.report.shareEnabled')
                : t('interviews.report.shareDisabled')}
            </Button>
            {interview.isPublic && shareUrl !== null && (
              <Button variant="ghost" size="sm" onClick={handleCopyShareLink}>
                {copiedLink
                  ? t('interviews.report.linkCopied')
                  : t('interviews.report.copyLink')}
              </Button>
            )}
            <Link
              to="/dashboard/jobs/$jobId"
              params={{ jobId: job._id }}
              className="text-sm text-primary/60 underline-offset-4 hover:text-primary hover:underline"
            >
              {t('interviews.report.back')}
            </Link>
          </div>
        </div>

        <div className="flex flex-col gap-6">
          <section className="flex flex-col gap-3">
            <h3 className="text-base font-semibold text-primary">
              {t('interviews.report.summary')}
            </h3>
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <p className="text-sm leading-relaxed text-primary/80">
                {interview.summary ?? t('interviews.report.noSummary')}
              </p>
            </div>
          </section>

          {interview.status === 'completed' && (
            <section className="flex flex-col gap-3">
              <h3 className="text-base font-semibold text-primary">
                {t('interviews.report.aiDetection')}
              </h3>
              {interview.aiDetectionScore === undefined ? (
                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <p className="text-sm text-primary/50">
                    {t('interviews.report.aiDetectionPending')}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-3 rounded-lg border border-border p-4">
                  <div className="flex items-center gap-3">
                    <span
                      className={cn(
                        'rounded-full px-3 py-1 text-xs font-medium',
                        interview.aiDetectionScore >= 0.7
                          ? 'bg-destructive/10 text-destructive'
                          : interview.aiDetectionScore >= 0.4
                            ? 'bg-amber-500/10 text-amber-600'
                            : 'bg-green-500/10 text-green-700',
                      )}
                    >
                      {interview.aiDetectionScore >= 0.7
                        ? t('interviews.report.aiDetectionHigh')
                        : interview.aiDetectionScore >= 0.4
                          ? t('interviews.report.aiDetectionMedium')
                          : t('interviews.report.aiDetectionLow')}
                    </span>
                    <span className="text-xs text-primary/50">
                      {t('interviews.report.aiDetectionScore')}:{' '}
                      {(interview.aiDetectionScore * 100).toFixed(0)}%
                    </span>
                  </div>
                  {interview.aiDetectionFlags !== undefined &&
                    interview.aiDetectionFlags.length > 0 && (
                      <div className="flex flex-col gap-2">
                        <span className="text-xs font-medium uppercase tracking-wide text-primary/40">
                          {t('interviews.report.aiDetectionFlags')}
                        </span>
                        {interview.aiDetectionFlags.map((flag, i) => (
                          <p
                            key={i}
                            className="border-l-2 border-amber-400/60 pl-3 text-xs italic text-primary/60"
                          >
                            {flag}
                          </p>
                        ))}
                      </div>
                    )}
                </div>
              )}
            </section>
          )}

          {interview.recordingR2Key && (
            <section className="flex flex-col gap-3">
              <h3 className="text-base font-semibold text-primary">
                {t('interviews.report.video')}
              </h3>
              {recordingUrl ? (
                <div className="relative">
                  <video
                    ref={videoRef}
                    src={recordingUrl}
                    controls
                    onTimeUpdate={() => {
                      if (videoRef.current) {
                        setCurrentTimeSec(videoRef.current.currentTime)
                      }
                    }}
                    className="w-full rounded-lg border border-border bg-black"
                  />
                </div>
              ) : (
                <div className="flex h-16 items-center justify-center rounded-lg border border-border bg-muted/30">
                  <p className="text-sm text-primary/40">
                    {t('interviews.report.videoLoading')}
                  </p>
                </div>
              )}
            </section>
          )}

          {!interview.recordingR2Key && (
            <section className="flex flex-col gap-3">
              <h3 className="text-base font-semibold text-primary">
                {t('interviews.report.video')}
              </h3>
              <p className="text-sm text-primary/40">
                {t('interviews.report.noVideo')}
              </p>
            </section>
          )}

          {competencyScores.length > 0 && (
            <section className="flex flex-col gap-3">
              <h3 className="text-base font-semibold text-primary">
                {t('interviews.report.competencies')}
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {competencyScores.map((score) => (
                  <div
                    key={score._id}
                    className="flex flex-col gap-3 rounded-lg border border-border p-4"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-primary">
                        {score.competency}
                      </span>
                      <span className="text-sm font-semibold text-primary">
                        {score.score}/{SCORE_MAX}
                      </span>
                    </div>
                    <ScoreBar score={score.score} />
                    <p className="text-sm text-primary/70">{score.rationale}</p>
                    {score.notableQuotes.length > 0 && (
                      <div className="flex flex-col gap-1">
                        <span className="text-xs font-medium uppercase tracking-wide text-primary/40">
                          {t('interviews.report.quotes')}
                        </span>
                        {score.notableQuotes.map((quote, i) =>
                          quote.startSec !== undefined ? (
                            <button
                              key={i}
                              type="button"
                              onClick={() => handleSeekVideo(quote.startSec!)}
                              className="border-l-2 border-primary/30 pl-3 text-left text-xs italic text-primary/60 hover:border-primary hover:text-primary"
                            >
                              {quote.text}
                            </button>
                          ) : (
                            <blockquote
                              key={i}
                              className="border-l-2 border-primary/30 pl-3 text-xs italic text-primary/60"
                            >
                              {quote.text}
                            </blockquote>
                          ),
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {competencyScores.length === 0 && (
            <section className="flex flex-col gap-3">
              <h3 className="text-base font-semibold text-primary">
                {t('interviews.report.competencies')}
              </h3>
              <p className="text-sm text-primary/40">
                {t('interviews.report.noScores')}
              </p>
            </section>
          )}

          {clips.length > 0 && clipUrls.length > 0 && (
            <section className="flex flex-col gap-3">
              <h3 className="text-base font-semibold text-primary">
                {t('interviews.report.clips')}
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {clipUrls.map((clip) => (
                  <div
                    key={clip.clipId}
                    className="flex flex-col gap-2 rounded-lg border border-border p-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-primary">
                        {clip.competency}
                      </span>
                      <span className="text-xs text-primary/50">
                        {t('interviews.report.clipDuration', {
                          sec: String(Math.round(clip.endSec - clip.startSec)),
                        })}
                      </span>
                    </div>
                    <video
                      src={clip.url}
                      controls
                      className="w-full rounded border border-border bg-black"
                    />
                  </div>
                ))}
              </div>
            </section>
          )}

          {clips.length === 0 && (
            <section className="flex flex-col gap-3">
              <h3 className="text-base font-semibold text-primary">
                {t('interviews.report.clips')}
              </h3>
              <p className="text-sm text-primary/40">
                {t('interviews.report.noClips')}
              </p>
            </section>
          )}

          <section className="flex flex-col gap-3">
            <h3 className="text-base font-semibold text-primary">
              {t('interviews.report.notes')}
            </h3>
            <NotesPanel
              interviewId={interviewId}
              notes={notesAndViewers?.notes ?? []}
            />
          </section>

          <section className="flex flex-col gap-3">
            <h3 className="text-base font-semibold text-primary">
              {t('interviews.chat.title')}
            </h3>
            <RagChatPanel interviewId={interviewId} />
          </section>

          <section className="flex flex-col gap-3">
            <h3 className="text-base font-semibold text-primary">
              {t('interviews.report.transcript')}
            </h3>
            {transcriptChunks.length > 0 ? (
              <TranscriptPanel
                chunks={transcriptChunks}
                currentTimeSec={currentTimeSec}
                onChunkClick={handleSeekVideo}
              />
            ) : interview.transcript ? (
              <pre className="max-h-96 overflow-y-auto rounded-lg border border-border bg-muted/30 p-4 text-sm leading-relaxed whitespace-pre-wrap text-primary/80">
                {interview.transcript}
              </pre>
            ) : (
              <p className="text-sm text-primary/40">
                {t('interviews.report.noTranscript')}
              </p>
            )}
          </section>
        </div>
      </div>
    </div>
  )
}
